import {
    Avatar,
    FormHelperText,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FormMultiAutocomplete from '../../../../shared/FormMultiAutocomplete';
import { useGetIndividualOwnerAssetsQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import { FormValues as Experience } from '../../../../types/experience';
import { ExperienceType, AvailabilityMode } from '../../../../enums/experience';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import { useFormState } from '../../Form/useFormState';

const AvailabilityModeSection = () => {
    const loggedInUser = UserRole();
    const { control, setValue } = useFormContext<Experience>();
    const { experience } = useFormState();

    const { data: experiences } = useGetIndividualOwnerAssetsQuery({
        url: `/owners/${loggedInUser.company}/modules`,
        type: [ExperienceType.Property, ExperienceType.Commercial],
        sort: 'name',
        status: 'published',
    });
    const availabilityMode = useWatch({ name: 'availabilityMode', control });

    const AVAILABILITY_MODE_OPTIONS = [
        {
            value: AvailabilityMode.Standalone,
            label: 'Standalone',
            description: 'Offered as an independent experience',
        },
        {
            value: AvailabilityMode.AddOn,
            label: 'Add-on',
            description: 'Only offered alongside another experience',
        },
        {
            value: AvailabilityMode.Both,
            label: 'Both',
            description:
                'Offered independently or alongside another experience',
        },
    ];

    const experienceOptions: Experience[] =
        experiences
            ?.filter((experience: Experience) => experience.isAvailable)
            .map((experience: Experience) => ({
                value: { id: experience.id },
                label: experience.name,
                ...experience,
            })) ?? [];

    useEffect(() => {
        if (availabilityMode === AvailabilityMode.Standalone) {
            setValue('addOnOf', []);
        }
    }, [availabilityMode]);

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h3'>Availability</Typography>
                    <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary' }}
                    >
                        Offer this {experience.type} as a standalone experience,
                        as an add-on to other experiences or as both.
                    </Typography>
                </div>
                <div>
                    <InputLabel htmlFor='availability-mode__input' required>
                        Availability Mode
                    </InputLabel>
                    <FormAutocomplete
                        id='availability-mode__input'
                        name='availabilityMode'
                        control={control}
                        options={AVAILABILITY_MODE_OPTIONS}
                        renderOption={(props, option) => (
                            <li key={option.value} {...props}>
                                <ListItemText
                                    primary={option.label}
                                    secondary={option.description}
                                />
                            </li>
                        )}
                    />
                </div>
                {availabilityMode !== AvailabilityMode.Standalone && (
                    <div>
                        <InputLabel htmlFor='add-on-of__input' required>
                            Available for
                        </InputLabel>
                        <FormMultiAutocomplete
                            id='add-on-of__input'
                            name='addOnOf'
                            control={control}
                            options={experienceOptions}
                            renderOption={(props, option) => (
                                <li key={option.id} {...props}>
                                    <ListItemAvatar>
                                        <Avatar
                                            variant='rounded'
                                            src={option.photos?.[0]?.src}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={option.name}
                                        secondary={`${option.streetAddress}, ${option.suburb}`}
                                    />
                                </li>
                            )}
                        />
                        <FormHelperText>
                            Which experiences should offer this{' '}
                            {experience.type} as an add-on?
                        </FormHelperText>
                    </div>
                )}
            </Stack>
        </fieldset>
    );
};

export default AvailabilityModeSection;
